import React, {
  useEffect, useState, useMemo, useContext
} from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import PdpOurPromise from "../pdp-our-promise";
import ProductImagesSlider from "components/product-images/slider";
import ProductVariations from "./product-variations";
import locale from "locales";
import { addCartItems, fetchCart } from "data/cart";
import { triggerProductAddedEventMable } from "helpers/analytics";
import { getReviewSummary } from "../../data/trusted-reviews";
import gaEvent from "helpers/ga-event";
import { trackAddedToCartKlaviyo } from "helpers/analytics";
import GlobalContext from "services/global-context";
import {
  container,
  contentCol,
  imagesCol,
  isDesktop,
  addToCartBtn,
  mainTitle
} from "./style.mod.scss";
import NewVariantCategoryLevel from "./new-variant-category-level";
import Bar from "./bar";
import ProductContext from "services/product-context";
import { Container } from "react-bootstrap";

const CoatedProductDetail = ({ data, reviewSummary }) => {
  const [ctaDisabled, setDisabled] = useState(false);
  const [coatCount, setCoatCount] = useState(1);
  const { toggleCart, cartOpened, addToCart: onAddToCart } = useContext(GlobalContext);
  const [pan] = useState(
    data?.reference
  );
  const [localReviewSummary, setLocalReviewSummary] = useState(null);
  const defaultVariant = pan?.currentVariant.find(variant => variant.handle?.current === "alleskoenner-26cm") || pan?.currentVariant[0];

  const [currentV, setCurrentV] = useState(defaultVariant);
  const variantPackage = currentV?.replacePackage?.id && !currentV?.store?.inStock ? currentV?.replacePackage : currentV;

  useEffect(() => {
    async function fetchData() {
      try {
        const review = await getReviewSummary(pan?.id);
        setLocalReviewSummary(review);
      } catch (error) {
        console.error("Error fetching review summary:", error);
      }
    }
    fetchData();
  }, [data]);

  const pdpLayout = pan?.content?.layout;
  const productHighlights = pan?.content?.enhancedPDP?.productHighlights;

  const filterEmptyCategories = (hierarchy) =>
    hierarchy.filter(
      (item) =>
        item.children.find((cat) => cat._type === "variant-category") !==
        undefined ||
        filterEmptyCategories(item.children).length !== 0
    );

  const getHierarchy = (variantCategories, allCategories, variants) => {
    if (variantCategories.length === 0) return [];

    return variantCategories.map((vc) => {
      const childCategories = getHierarchy(
        allCategories.filter(
          (cat) => cat.parentCategory?._ref === vc._id
        ),
        allCategories,
        variants
      ).map((cat) => ({
        ...cat,
        parent: vc,
      }));

      return {
        ...vc,
        children:
          childCategories.length === 0
            ? variants?.filter(
              (variant) =>
                variant.variantCategory?._ref === vc._id
            )
            : childCategories,
      };
    });
  };

  const getParents = (category, allCategories) => {
    if (!category.parentCategory) return [category];
    const parent = allCategories.find(
      (cat) => cat._id === category.parentCategory._ref
    );

    return [category, parent, ...getParents(parent, allCategories)].flat();
  };

  const getSelectedNodes = (variantCategories, currentVariant) => {
    const { variantCategory } = currentVariant || {};
    if (!variantCategory) return [];

    const matchingCategory = variantCategories.find(
      (vc) => vc._id === variantCategory._ref
    );

    return getParents(matchingCategory, variantCategories).map(
      (vc) => vc._id
    );
  };

  const getVariantCategories = (variantCategories, selected) =>
    variantCategories.map((cat) => ({
      ...cat,
      selected: selected.indexOf(cat._id) !== -1,
    }));

  const selectedCategoryIds = useMemo(
    () => getSelectedNodes(pan?.variantCategories, currentV),
    [pan?.variantCategories, currentV]
  );

  const hierarchy = useMemo(() => {
    const mappedCategories = getVariantCategories(
      pan?.variantCategories,
      selectedCategoryIds
    );
    const rootCategories = mappedCategories.filter(
      (cat) => !cat.parentCategory
    );

    return filterEmptyCategories(
      getHierarchy(
        rootCategories,
        mappedCategories,
        pan?.currentVariant
      )
    );
  }, [pan?.variantCategories, currentV]);

  const categories = hierarchy.filter(
    (item) =>
      item.internal_id ===
      "einzelne-olav-pfanne-beschichtet"
  );

  const handleCategorySelection = (categoryId) => {
    const selectedVariant = pan?.currentVariant.find(
      (variant) =>
        variant.variantCategory?._ref ===
        categoryId || variant._id === categoryId
    );
    setCurrentV(selectedVariant);
  };

  const handleAddToCart = async () => {
    triggerProductAddedEventMable(currentV);
    setDisabled(true);

    let productsToAdd = [];

    productsToAdd.push(variantPackage);

    try {
      if (productsToAdd.length > 0) {
        await addCartItems(productsToAdd.map(variant => variant?.id));
        console.log("Products added to cart successfully.");
      }

      gaEvent("PDP", "Click", "Added to cart");

      if (!cartOpened) {
        toggleCart();
      }

      const cart = await fetchCart();

      trackAddedToCartKlaviyo(cart, currentV);
      setDisabled(false);

    } catch (error) {
      console.error("Error adding to cart:", error);
      setDisabled(false);
    }
  };

  return (
    <ProductContext.Provider
      value={{
        pan,
        currentV,
        setCurrentV,
        reviewSummary: localReviewSummary || reviewSummary,
        onAddToCart,
        coatCount,
        setCoatCount
      }}
    >
      <Container id="coated-buy-box">
        <div className={`${container}`}>
          <h1 className={mainTitle}>{data?.title}</h1>
          <Row style={{ justifyContent: "center" }}>
            <Col lg={8} className={imagesCol} style={{ paddingLeft: 0 }}>
              <ProductImagesSlider
                key={currentV?._id}
                currentVariant={currentV}
                content={pan?.content}
                reviewSummary={localReviewSummary || reviewSummary}
                enhancedPDP={pdpLayout === "enhanced"}
                slidesPerView={4.5}
                spaceBetween={5}
              />
              <div className={isDesktop}>
                <PdpOurPromise
                  variant={currentV}
                  handle={pan?.handle}
                  color={pan?.content?.bgColor}
                  enhancedPDP={pdpLayout === "enhanced"}
                  productHighlights={productHighlights}
                />
              </div>
            </Col>
            <Col lg={4} className={`${contentCol}`}>
              <ProductVariations />
              {categories.length > 0 && (
                <NewVariantCategoryLevel
                  categories={categories[0]?.children}
                  onCategorySelect={handleCategorySelection}
                />
              )}
              <button id="addToCart" className={addToCartBtn} onClick={handleAddToCart} disabled={ctaDisabled}><p>{locale.products.addToCart}</p> </button>
              {data?.bar && <Bar data={data?.bar} />}
            </Col>
          </Row>
        </div>
      </Container>
    </ProductContext.Provider>
  );
};

export default CoatedProductDetail;
